import type { ImageReference } from "@metalmind-ab/mmagent-chat-ui";
import { getToolDisplayName } from "./toolDisplayName";
import type { LlmRouteProvenance } from "../components/llmRouteProvenanceTypes";

/**
 * Canonical notice sentence shown when an assistant answer was interrupted
 * before it completed (issue #4981). SINGLE source of truth: the persisted-row
 * notice and the passive stream render this string; the live-stream sites
 * (useSendMessageWithFiles.ts, utils/chatResumeOptions.ts, views/Chat.tsx)
 * decorate STREAM_INTERRUPTED_MARKER with markdown around the same sentence.
 * Do not duplicate the text anywhere else.
 */
export const INTERRUPTED_NOTICE_TEXT =
  "Connection interrupted — response may be incomplete. Send your prompt again to retry.";
export const STOPPED_BY_USER_NOTICE_TEXT = "Stopped by you.";

export interface HeartbeatTurnMetadata {
  origin?: string;
  task_id?: string;
  /**
   * Backend-authored LLM route provenance, persisted onto assistant message
   * metadata by the gateway and preserved through the mapper/poll path. The UI
   * only displays these fields; it never computes them.
   */
  _ida_llm_route_provenance?: LlmRouteProvenance;
  [key: string]: unknown;
}

export interface HubConversationToolExecution {
  id: string;
  name: string;
  args?: Record<string, unknown>;
  status?:
    "running" | "completed" | "exited" | "error" | "pending_confirmation";
  result?: Record<string, unknown>;
  error?: string;
  content_position?: number;
  replaces_tool_call_id?: string;
}

export interface HubConversationToolCall {
  id: string;
  type?: "function";
  function: {
    name: string;
    arguments?: string;
  };
}

export interface HubConversationContentMessage {
  id: string | number;
  over_cap?: false;
  role: "user" | "assistant" | "system" | "tool" | "job_completion";
  content: string;
  tool_calls?: HubConversationToolCall[];
  tool_call_id?: string;
  tool_name?: string;
  created_at: string;
  tool_executions?: HubConversationToolExecution[];
  metadata?: HeartbeatTurnMetadata;
  // Gateway-migrated assistant rows carry top-level LLM usage that the FE
  // surfaces on the per-message usage chip. Backend-authored and displayed
  // verbatim; the UI computes nothing from them (it only sums nothing here —
  // it just passes them through the mapper).
  model?: string;
  provider?: string;
  prompt_tokens?: number;
  completion_tokens?: number;
  total_tokens?: number;
  // Hub PR #13 (SDK 0.4.11+): BE marks intermediate iteration rows that
  // duplicate-prefix the final cumulative row as `visible_in_chat: false`.
  // Default true when absent (legacy rows / older BE versions).
  visible_in_chat?: boolean;
}

/** An unread oversized Hub row, identified without decoding its content. */
export interface HubConversationOverCapMessage {
  id: number;
  over_cap: true;
  size_class: string;
  role?: never;
  content?: never;
  created_at?: never;
}

export type HubConversationMessage =
  HubConversationContentMessage | HubConversationOverCapMessage;

export interface IDAChatMessage {
  id: string;
  role: "user" | "assistant" | "system" | "tool" | "job_completion";
  content: string;
  images?: ImageReference[];
  /** Documents attached to a user message (persisted file_ref blocks). */
  fileAttachments?: {
    filename: string;
    fileSize: number;
    mimeType: string;
    docId?: string;
  }[];
  /** Internal display sentinel; not decoded message content. */
  overCap?: { kind: "over_cap"; sizeClass: string };
  /**
   * True when the backend persisted this assistant answer as interrupted
   * (metadata.interrupted, issue #4981). Set by the mapper from the Hub row
   * metadata; the renderer shows the interruption notice on these rows.
   */
  interrupted?: boolean;
  /** True when the current client confirmed a user-initiated Stop for this turn. */
  stoppedByUser?: boolean;
  toolCalls?: {
    id: string;
    type: "function";
    function: {
      name: string;
      arguments: string;
    };
  }[];
  toolCallId?: string;
  toolName?: string;
  createdAt: Date;
  isStreaming?: boolean;
  /** Typed server error code on transient SSE error rows. */
  errorCode?: string;
  errorSource?: string;
  toolExecutions?: {
    id: string;
    name: string;
    args: Record<string, unknown>;
    status:
      "running" | "completed" | "exited" | "error" | "pending_confirmation";
    result?: Record<string, unknown>;
    error?: string;
    replaces_tool_call_id?: string;
    contentPosition?: number;
  }[];
  citations?: any[];
  jobCompletion?: any;
  hubMessageId?: number;
  hubMessageIds?: number[];
  metadata?: HeartbeatTurnMetadata;
  // Top-level LLM usage carried on gateway-migrated assistant messages,
  // preserved through the mapper for the per-message usage chip. Displayed
  // verbatim.
  model?: string;
  provider?: string;
  prompt_tokens?: number;
  completion_tokens?: number;
  total_tokens?: number;
}

type ToolCalls = NonNullable<IDAChatMessage["toolCalls"]>;
type ToolExecutions = NonNullable<IDAChatMessage["toolExecutions"]>;
type Citations = NonNullable<IDAChatMessage["citations"]>;
type ToolExecution = ToolExecutions[number];

function parseToolArgs(argumentsJson?: string): Record<string, unknown> {
  if (!argumentsJson) return {};

  try {
    return JSON.parse(argumentsJson) as Record<string, unknown>;
  } catch {
    return {};
  }
}

function parseHubMessageId(id: string | number): number | undefined {
  if (typeof id === "number" && Number.isInteger(id)) {
    return id;
  }

  if (typeof id === "string" && /^\d+$/.test(id)) {
    return parseInt(id, 10);
  }

  return undefined;
}

function parseUserImageContent(content: string): {
  content: string;
  images?: ImageReference[];
  fileAttachments?: NonNullable<IDAChatMessage["fileAttachments"]>;
} {
  if (!content.trimStart().startsWith("[")) return { content };
  try {
    const blocks: unknown = JSON.parse(content);
    if (!Array.isArray(blocks) || blocks[0]?.type !== "text")
      return { content };
    const texts: string[] = [];
    const images: ImageReference[] = [];
    const fileAttachments: NonNullable<IDAChatMessage["fileAttachments"]> = [];
    for (const block of blocks) {
      if (!block || typeof block !== "object") return { content };
      if (block.type === "text" && typeof block.text === "string") {
        texts.push(block.text);
      } else if (
        block.type === "image_ref" &&
        typeof block.attachment_id === "string" &&
        ["image/png", "image/jpeg", "image/webp"].includes(block.media_type) &&
        typeof block.size_bytes === "number"
      ) {
        images.push({
          attachment_id: block.attachment_id,
          media_type: block.media_type,
          size_bytes: block.size_bytes,
        });
      } else if (
        block.type === "file_ref" &&
        typeof block.doc_id === "string" &&
        typeof block.filename === "string" &&
        typeof block.file_size === "number"
      ) {
        fileAttachments.push({
          docId: block.doc_id,
          filename: block.filename,
          fileSize: block.file_size,
          mimeType:
            typeof block.mime_type === "string"
              ? block.mime_type
              : "application/octet-stream",
        });
      } else {
        return { content };
      }
    }
    return images.length > 0 || fileAttachments.length > 0
      ? {
          content: texts.join("\n"),
          ...(images.length > 0 ? { images } : {}),
          ...(fileAttachments.length > 0 ? { fileAttachments } : {}),
        }
      : { content };
  } catch {
    return { content };
  }
}

function normalizePersistedToolStatus(
  execution: HubConversationToolExecution,
): NonNullable<HubConversationToolExecution["status"]> {
  if (execution.status === "completed" && execution.result?.success === false) {
    return "error";
  }

  return execution.status || "completed";
}

export function getHeartbeatMetadata(
  message: Pick<IDAChatMessage, "role" | "metadata">,
): HeartbeatTurnMetadata | undefined {
  if (message.role !== "system") return undefined;
  if (message.metadata?.origin !== "scheduled_fire") return undefined;
  return message.metadata;
}

function normalizeToolCalls(
  toolCalls?: HubConversationToolCall[],
): ToolCalls | undefined {
  return toolCalls?.map((toolCall) => ({
    ...toolCall,
    type: "function" as const,
    function: {
      ...toolCall.function,
      name:
        getToolDisplayName(toolCall.function.name) || toolCall.function.name,
      arguments: toolCall.function.arguments || "{}",
    },
  })) as ToolCalls | undefined;
}

function mapStandaloneToolExecutions(
  message: HubConversationContentMessage,
): ToolExecutions | undefined {
  if (message.tool_executions && message.tool_executions.length > 0) {
    return message.tool_executions.map((execution) => ({
      id: execution.id,
      name: getToolDisplayName(execution.name) || execution.name,
      args: execution.args || {},
      status: normalizePersistedToolStatus(execution),
      result: execution.result,
      error: execution.error,
      replaces_tool_call_id: execution.replaces_tool_call_id,
      contentPosition:
        execution.content_position ?? message.content?.length ?? 0,
    })) as ToolExecutions;
  }

  if (message.tool_calls && message.tool_calls.length > 0) {
    return message.tool_calls.map((toolCall) => ({
      id: toolCall.id,
      name:
        getToolDisplayName(toolCall.function.name) || toolCall.function.name,
      args: parseToolArgs(toolCall.function.arguments),
      status: "completed" as const,
      contentPosition: message.content?.length ?? 0,
    })) as ToolExecutions;
  }

  return undefined;
}

function collectCitations(
  messages: HubConversationContentMessage[],
): Citations | undefined {
  const persistedInlineCitations = messages.flatMap((message) => {
    const inlineCitations = message.metadata?.inline_citations;
    return Array.isArray(inlineCitations) ? inlineCitations : [];
  });
  if (persistedInlineCitations.length > 0) {
    return persistedInlineCitations as Citations;
  }

  const citations: unknown[] = [];

  for (const message of messages) {
    if (!message.tool_executions) continue;

    for (const execution of message.tool_executions) {
      const executionCitations = execution.result?.citations;
      if (Array.isArray(executionCitations)) {
        citations.push(...executionCitations);
      }
    }
  }

  return citations.length > 0 ? (citations as Citations) : undefined;
}

function mapStandaloneMessage(
  message: HubConversationContentMessage,
): IDAChatMessage {
  const hubMessageId = parseHubMessageId(message.id);
  const userContent =
    message.role === "user"
      ? parseUserImageContent(message.content)
      : { content: message.content };

  return {
    id: String(message.id),
    hubMessageId,
    hubMessageIds: hubMessageId !== undefined ? [hubMessageId] : undefined,
    role: message.role,
    ...userContent,
    toolCalls: normalizeToolCalls(message.tool_calls),
    toolCallId: message.tool_call_id,
    toolName: getToolDisplayName(message.tool_name) || message.tool_name,
    createdAt: new Date(message.created_at),
    isStreaming: false,
    toolExecutions: mapStandaloneToolExecutions(message),
    citations: collectCitations([message]),
    interrupted: message.metadata?.interrupted === true,
    stoppedByUser: message.metadata?.interrupted_reason === "user_stopped",
    metadata: message.metadata,
    model: message.model,
    provider: message.provider,
    prompt_tokens: message.prompt_tokens,
    completion_tokens: message.completion_tokens,
    total_tokens: message.total_tokens,
  };
}

/** True when a row carries any top-level LLM token usage field. */
function hasTopLevelUsage(message: HubConversationContentMessage): boolean {
  return (
    message.model != null ||
    message.provider != null ||
    message.prompt_tokens != null ||
    message.completion_tokens != null ||
    message.total_tokens != null
  );
}

/** True for a persisted GPT-Live transcript row, rather than a delegated IDA
 * turn.
 */
function isGptLiveTranscriptRow(
  message: HubConversationContentMessage,
): boolean {
  if (message.role !== "assistant" || message.metadata?.source !== "voice") {
    return false;
  }

  return (
    (typeof message.metadata.voice_turn_id === "string" &&
      message.metadata.voice_turn_id.length > 0) ||
    (typeof message.metadata.start_ms === "number" &&
      Number.isFinite(message.metadata.start_ms))
  );
}

function upsertToolExecution(
  executionsById: Map<string, ToolExecution>,
  orderedExecutionIds: string[],
  execution: ToolExecution,
): void {
  const existingExecution = executionsById.get(execution.id);

  if (!existingExecution) {
    executionsById.set(execution.id, execution);
    orderedExecutionIds.push(execution.id);
    return;
  }

  executionsById.set(execution.id, {
    ...existingExecution,
    ...execution,
    args:
      Object.keys(execution.args || {}).length > 0
        ? execution.args
        : existingExecution.args,
    result: execution.result ?? existingExecution.result,
    error: execution.error ?? existingExecution.error,
    contentPosition:
      existingExecution.contentPosition ?? execution.contentPosition,
  });
}

function mergeAssistantContent(
  existingContent: string,
  nextContent: string,
): { content: string; offset: number } {
  if (!nextContent.trim()) {
    return { content: existingContent, offset: existingContent.length };
  }

  if (!existingContent) {
    return { content: nextContent, offset: 0 };
  }

  // Some backend turns persist a cumulative assistant row after a tool call,
  // where the later row already includes the earlier pre-tool text as a prefix.
  // Prefer the cumulative row instead of rendering the shared prefix twice.
  if (nextContent.startsWith(existingContent)) {
    return { content: nextContent, offset: 0 };
  }

  return {
    content: `${existingContent}\n\n${nextContent}`,
    offset: existingContent.length + 2,
  };
}

function mapAssistantCluster(
  clusterMessages: HubConversationContentMessage[],
): IDAChatMessage {
  const assistantMessages = clusterMessages.filter(
    (message) => message.role === "assistant",
  );
  const firstAssistantMessage = assistantMessages[0];
  const hubMessageIds: number[] = [];
  let combinedContent = "";
  const toolCallsById = new Map<string, ToolCalls[number]>();
  const executionsById = new Map<string, ToolExecution>();
  const orderedExecutionIds: string[] = [];

  for (const message of assistantMessages) {
    const assistantHubMessageId = parseHubMessageId(message.id);
    if (assistantHubMessageId !== undefined) {
      hubMessageIds.push(assistantHubMessageId);
    }

    const messageContent = message.content || "";
    const { content, offset: contentOffset } = mergeAssistantContent(
      combinedContent,
      messageContent,
    );
    combinedContent = content;

    const normalizedToolCalls = normalizeToolCalls(message.tool_calls);
    normalizedToolCalls?.forEach((toolCall) => {
      if (!toolCallsById.has(toolCall.id)) {
        toolCallsById.set(toolCall.id, toolCall);
      }

      upsertToolExecution(executionsById, orderedExecutionIds, {
        id: toolCall.id,
        name:
          getToolDisplayName(toolCall.function.name) || toolCall.function.name,
        args: parseToolArgs(toolCall.function.arguments),
        status: "completed",
        contentPosition: contentOffset + messageContent.length,
      });
    });

    message.tool_executions?.forEach((execution) => {
      upsertToolExecution(executionsById, orderedExecutionIds, {
        id: execution.id,
        name: getToolDisplayName(execution.name) || execution.name,
        args: execution.args || {},
        status: normalizePersistedToolStatus(execution),
        result: execution.result,
        error: execution.error,
        replaces_tool_call_id: execution.replaces_tool_call_id,
        contentPosition:
          execution.content_position !== undefined
            ? contentOffset + execution.content_position
            : contentOffset + messageContent.length,
      });
    });
  }

  // A paged history read can split the hidden instructions row and the
  // execution it precedes across pages. The API links calls when both are in
  // one page; once older pages are merged, recover the same relation across
  // this complete assistant turn. The handshake is once per skill per
  // conversation, so only the first later same-name execution is the retry.
  const orderedExecutions = orderedExecutionIds
    .map((id) => executionsById.get(id))
    .filter((execution): execution is ToolExecution => Boolean(execution));
  orderedExecutions.forEach((instruction, index) => {
    if (
      instruction.result?.status !== "instructions" ||
      instruction.result.executed !== false ||
      orderedExecutions.some(
        (execution) => execution.replaces_tool_call_id === instruction.id,
      )
    ) {
      return;
    }
    const skillName =
      typeof instruction.result.skill === "string"
        ? instruction.result.skill
        : instruction.name;
    const replacement = orderedExecutions.slice(index + 1).find((execution) => {
      return (
        execution.name === skillName &&
        execution.result?.status !== "instructions" &&
        !execution.replaces_tool_call_id
      );
    });
    if (replacement && !replacement.replaces_tool_call_id) {
      replacement.replaces_tool_call_id = instruction.id;
    }
  });

  const primaryHubMessageId = hubMessageIds[0];

  // Gateway usage/provenance is authored on the latest underlying assistant
  // row (the post-tool summary turn). Take provenance metadata from the last
  // provenance-bearing row and top-level token usage from the last
  // usage-bearing row, so the chip reflects the final turn. Fall back to the
  // first row's metadata when no row carries provenance, preserving prior
  // behavior for clusters without gateway usage.
  const provenanceSource = [...assistantMessages]
    .reverse()
    .find((message) => message.metadata?._ida_llm_route_provenance != null);
  const usageSource = [...assistantMessages].reverse().find(hasTopLevelUsage);

  // #4981: the BE flags the interrupted row in a cluster via
  // metadata.interrupted; a cluster shows the notice if ANY underlying
  // assistant row carries it.
  const clusterInterrupted = assistantMessages.some(
    (message) => message.metadata?.interrupted === true,
  );
  const clusterStoppedByUser = assistantMessages.some(
    (message) =>
      message.metadata?.interrupted === true &&
      message.metadata?.interrupted_reason === "user_stopped",
  );

  return {
    id: String(firstAssistantMessage.id),
    interrupted: clusterInterrupted,
    stoppedByUser: clusterStoppedByUser,
    hubMessageId: primaryHubMessageId,
    hubMessageIds: hubMessageIds.length > 0 ? hubMessageIds : undefined,
    role: "assistant",
    content: combinedContent,
    isStreaming: false,
    toolCalls:
      toolCallsById.size > 0
        ? (Array.from(toolCallsById.values()) as ToolCalls)
        : undefined,
    createdAt: new Date(firstAssistantMessage.created_at),
    toolExecutions:
      orderedExecutionIds.length > 0
        ? (orderedExecutionIds
            .map((executionId) => executionsById.get(executionId))
            .filter((execution): execution is ToolExecution =>
              Boolean(execution),
            ) as ToolExecutions)
        : undefined,
    citations: collectCitations(assistantMessages),
    metadata: (provenanceSource ?? firstAssistantMessage).metadata,
    model: usageSource?.model,
    provider: usageSource?.provider,
    prompt_tokens: usageSource?.prompt_tokens,
    completion_tokens: usageSource?.completion_tokens,
    total_tokens: usageSource?.total_tokens,
  };
}

export function mapConversationMessages(
  rawMessages: HubConversationMessage[],
): IDAChatMessage[] {
  // Case B Rule 1: drop BE rows explicitly marked `visible_in_chat: false`
  // (Hub PR #13 / SDK 0.4.11+ flags intermediate iteration rows that the BE
  // doesn't want surfaced to chat). Treat `undefined` as visible (legacy/older
  // BE rows pre-flag default to render).
  const filteredMessages = rawMessages.filter(
    (message) => message.over_cap || message.visible_in_chat !== false,
  );

  const mappedMessages: IDAChatMessage[] = [];

  for (let index = 0; index < filteredMessages.length;) {
    const message = filteredMessages[index];

    // An unread row is a hard boundary even between assistant and tool rows.
    if (message.over_cap) {
      mappedMessages.push({
        id: String(message.id),
        hubMessageId: message.id,
        hubMessageIds: [message.id],
        // The chat UI requires a role and timestamp; neither is displayed or
        // attributed to the unread Hub row.
        role: "system",
        content: "",
        createdAt: new Date(0),
        overCap: { kind: "over_cap", sizeClass: message.size_class },
      });
      index += 1;
      continue;
    }

    if (message.role === "tool") {
      index += 1;
      continue;
    }

    if (message.role !== "assistant") {
      mappedMessages.push(mapStandaloneMessage(message));
      index += 1;
      continue;
    }

    // GPT-Live transcript rows are their own messages. Keep them out of the
    // following delegated IDA assistant/tool cluster, whose voice metadata
    // intentionally has no transcript timing or voice_turn_id.
    if (isGptLiveTranscriptRow(message)) {
      mappedMessages.push(mapStandaloneMessage(message));
      index += 1;
      continue;
    }

    const assistantCluster: HubConversationContentMessage[] = [message];
    let nextIndex = index + 1;

    while (nextIndex < filteredMessages.length) {
      const nextMessage = filteredMessages[nextIndex];
      if (nextMessage.over_cap) break;
      if (isGptLiveTranscriptRow(nextMessage)) break;
      if (nextMessage.role !== "assistant" && nextMessage.role !== "tool") {
        break;
      }

      assistantCluster.push(nextMessage);
      nextIndex += 1;
    }

    mappedMessages.push(mapAssistantCluster(assistantCluster));
    index = nextIndex;
  }

  return mappedMessages;
}
